import React from 'react';
import styles from './EditUserRoleModal.module.css';
import { FloComponentProps } from 'components';
import { FloModal } from 'components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Message from 'containers/common/Message';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FloFormGroup, FloModalBodyProps } from 'components';
import { User } from 'model';
import { FloDropdown } from 'components/FloDropdown';
import { createAccountRolesList, getAccountRoleFromPermissions, getAccountPermissions, RoleType, PermissionsEnum, getPermissionKey } from 'model/Role';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


type RoleRowProps = {
  label: string;
  isSelected: boolean;
  isLast: boolean;
}

const RoleRow: React.FC<RoleRowProps> = React.memo(({label, isSelected, isLast}) => (
  <>
    <Row className={styles.optionRow} >
      <Col xs="6"><span>{label}</span></Col>
      <Col xs="6" className={styles.iconWrapper}>{isSelected ? <FontAwesomeIcon className={styles.icon} icon="check" /> : null }</Col>
    </Row>
    { !isLast && <Row className={styles.dividerWrapper}><Col xs="12" className={styles.divider}></Col></Row> }
  </>
))

const roleList = [ PermissionsEnum.USER_CREATE, PermissionsEnum.USER_REMOVE, PermissionsEnum.USER_UPDATE, PermissionsEnum.WRITE ];

interface EditUserRoleBodyProps extends FloModalBodyProps {
  isLoading?:           boolean;
  currentUser?:         User;
  loggedUser?:          User;
  editRoles:            (user: User) => void;
  isSubmitting:         boolean;
  accountId?:           string;
}

const EditUserRoleBody: React.FC<EditUserRoleBodyProps> = React.memo(({
  bindIsFormValid,
  bindSubmitForm,
  isLoading,
  currentUser,
  loggedUser,
  editRoles,
  isSubmitting,
  accountId,
}) => {
  const { t } = useTranslation();
  const account = accountId ? { id: accountId } : undefined;

  return (
    <>
      <Message />
      <Formik
        enableReinitialize
        initialValues={{
          role: getAccountRoleFromPermissions(currentUser?.accountRole?.roles || []) || '',
        }}
        onSubmit={(values, actions) => {
          const accountRole = {
            accountId: accountId || '',
            roles: getAccountPermissions(values.role as RoleType)
          }
          editRoles({
            accountRole,
            email: currentUser?.email || '',
            account,
            id: currentUser?.id
          });
          actions.setSubmitting(false);
        }}
        validationSchema={Yup.object().shape({
          role: Yup.string().required(t('validation_required', { field: t('Role') })),
        })}
      >
        {({ values, handleBlur, handleChange, errors, touched, isValid, handleSubmit, submitForm, setFieldValue, setFieldTouched }) => {
          bindSubmitForm(submitForm);
          bindIsFormValid(isValid);

          const setField = (name: string, value: any) => {
            setFieldValue(name, value, true);
            setFieldTouched(name, true, false);
          };

          return (
            <Form className={styles.form} onSubmit={handleSubmit}>
              <FloFormGroup
                controlId   = "role"
                label       = {t('Role')}
                touched     = {touched}
                errors      = {errors}
              >
                <FloDropdown
                  id      = "role"
                  title   = {t('Role')}
                  values  = {values.role}
                  disabled = {isSubmitting || isLoading}
                  full
                  options = {createAccountRolesList(loggedUser).map(role => ({ value: role.roleKey, label: t(`account-${role.roleKey}`) }))}
                  onChange = {e => setField('role', e.value)}
                  isInvalid={!!(touched.role && errors.role)}
                />
              </FloFormGroup>


              <RoleRow
                label       = {t('Read')}
                isLast      = {false}
                isSelected
              />
              { roleList.map((role, ind) => (
                <RoleRow
                  key         = {`role-${ind}` }
                  label       = {t(getPermissionKey(role))}
                  isSelected  = {getAccountPermissions(values.role as RoleType).includes(role)}
                  isLast      = {roleList.length === ind + 1}
                />
              ))}
            </Form>
          )
        }}
      </Formik>
    </>
  )
})

export interface EditUserRoleModalProps extends FloComponentProps {
  show:                 boolean;
  onHide:               () => void;
  isLoading:            boolean;
  currentUser?:         User;
  editRoles:            (user: User) => void;
  isSubmitting:         boolean;
  accountId?:           string;
  loggedUser?:          User;
}

export const EditUserRoleModal: React.FC<EditUserRoleModalProps> = React.memo(({isLoading, currentUser, accountId, editRoles, isSubmitting, loggedUser, ...rest}) => {
  const { t } = useTranslation();
  return (
    <FloModal
      {...rest}
      name           = "edit-user-role"
      title          = {t('Edit Role/Permissions')}
      isSubmitting   = {isSubmitting}
      disableButtons = {isSubmitting}
      body           = { (props) => <EditUserRoleBody
                         {...props}
                         isLoading            = {isLoading}
                         currentUser          = {currentUser}
                         loggedUser           = {loggedUser}
                         editRoles            = {editRoles}
                         isSubmitting         = {isSubmitting}
                         accountId            = {accountId}
                      />}
    />
  );
})

export default EditUserRoleModal;
